/* eslint-disable react/display-name */
import React from 'react';
import styled from 'styled-components';
import useInView from 'react-cool-inview';

import { Container, Spinner, Label } from 'ui/components/styled';
import { spacing } from 'ui/theme/utils';
import Header from './Header';
import { chunk } from './helpers';

const defaultCountOptions = {
  prefixText: 'Showing',
  separatorText: 'of',
  suffixText: 'entries',
};

export const CardGrid = ({
  data = [],
  total = 0,
  loading,
  headerTitle,
  headerTitleProps,
  headerProps,
  headerActions,
  headerDisposition,
  filterPlaceholder,
  filterWidth,
  filterValue,
  openMobileFilter,
  onFilter,
  advancedSearch = false,
  advancedSearchOpen,
  setAdvancedSearchOpen,
  subHeader: SubHeader,
  cardRenderer = (data) => <Card>{JSON.stringify(data)}</Card>,
  cardsPerRow = 1,
  gap = '15px',
  noDataMessage = 'There are no records to display.',
  overflow = 'auto',
  onLoadMore,
  containerRef,
  currentPage,
  showCount = false,
  countOptions: initialCountOptions = {},
  girdTemplateColumns,
}) => {
  const ref = React.useRef();
  const hasMore = data.length < total;
  const countOptions = { ...defaultCountOptions, ...initialCountOptions };
  const count = React.useMemo(
    () =>
      showCount && total > 0 && (!loading || data.length > 0)
        ? currentPage === total
          ? `${countOptions.prefixText} 1-${total} ${countOptions.separatorText} ${total} ${countOptions.suffixText}`
          : `${countOptions.prefixText} 1-${data.length} ${countOptions.separatorText} ${total} ${countOptions.suffixText}`
        : '',
    [showCount, total, currentPage, data, countOptions]
  );
  const rows = React.useMemo(() => chunk(data, cardsPerRow), [data, cardsPerRow]);

  const { observe } = useInView({
    root: containerRef ? containerRef.current : ref.current,
    //rootMargin: '50px 0px',
    onEnter: ({ unobserve }) => {
      if (loading) return;
      if (!hasMore) {
        unobserve();
        return;
      }

      // Load more data
      if (onLoadMore) {
        onLoadMore();
      }
    },
  });

  const handleFilter = (value) => {
    if (onFilter) {
      onFilter(value);
    }
  };

  return (
    <>
      <Header
        title={headerTitle}
        titleProps={headerTitleProps}
        actions={headerActions}
        disposition={headerDisposition}
        filterPlaceholder={filterPlaceholder}
        filterWidth={filterWidth}
        filterValue={filterValue}
        openMobileFilter={openMobileFilter}
        onFilter={handleFilter}
        advancedSearch={advancedSearch}
        advancedSearchOpen={advancedSearchOpen}
        setAdvancedSearchOpen={setAdvancedSearchOpen}
        count={count}
        px="m"
        pr="l"
        {...headerProps}
      />
      {SubHeader && <SubHeader />}
      <Container ref={ref} overflow={overflow} px="m" pt="xxs">
        <CardContainer gap={gap} height="auto" pb="m" girdTemplateColumns={girdTemplateColumns}>
          {data?.length
            ? rows.map((row, index) => (
                <React.Fragment key={index}>
                  {row.map((cardData, cardIndex) => (
                    <CardWrapper
                      data-testid="card"
                      key={cardIndex}
                      data={cardData}
                      renderer={cardRenderer}
                      ref={index === rows.length - 1 ? observe : null}
                    />
                  ))}
                </React.Fragment>
              ))
            : !loading && <StyledLabel>{noDataMessage}</StyledLabel>}
        </CardContainer>
        {loading && <Loader />}
      </Container>
    </>
  );
};

const Loader = () => (
  <SpinnerContainer>
    <Spinner centered />
  </SpinnerContainer>
);

const SpinnerContainer = styled(Container)`
  height: auto;
  min-height: 100px;
  align-items: center;
`;

const CardWrapper = styled.div.attrs(({ renderer, data }) => ({
  children: React.createElement(renderer, data),
}))``;

const CardContainer = styled(Container)`
  display: grid;
  grid-template-columns: ${({ girdTemplateColumns = '1fr' }) => girdTemplateColumns};
`;

export const Card = styled.div`
  width: ${({ width = '100%' }) => width};
  height: ${({ height = '250px' }) => height};
  border-radius: 5px;
  background-color: #fff;
  box-shadow: 0px 1px 6px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  ${({ cursor }) => cursor && `cursor: ${cursor};`}
  ${(props) => spacing(props)}
`;

const StyledLabel = styled(Label).attrs(() => ({
  variant: 'legend',
  py: 'm',
}))`
  width: 100%;
  text-align: center;
`;
